// [목적] Settings 하위 9개 페이지 (프로필편집/이메일/비밀번호/취향/내보내기/약관/고객센터/탈퇴 + 신규)
// [이유] settings-be-wire — PATCH /users/me 실연결.
//         ProfileEditScreen(B) / TasteScreen(C) → usePatchUserMe + useMe 실배선.
//         EmailScreen(F) / PasswordScreen(G) / ExportScreen(I) → BE 미제공 안내 카드.
//         PolicyScreen(J) / HelpScreen(J) → 정적 콘텐츠 유지.
//         2026-05-17 profile-edit-polish —
//           (1) 아바타 URL 텍스트 입력 제거 (이미지 업로드 Spec 으로 분리, UX 노이즈 감소).
//           (2) locale 텍스트 입력 → Select dropdown (한국어/English/日本語).
//           (3) bio 표시 버그 해소는 BE DDL 11_users_profile_extension.sql + entity/repo/service 매핑 보강으로 처리.
//         fe-be-gap-tier3 T13/T14 —
//           DeleteAccountScreen(H): DELETE /users/me 구현 완료 → 확인 단계 + 실 탈퇴 플로우 활성.
//             soft-delete(ADR-T3-4) + handle 재사용 불가(ADR-T3-5).
//             비활성 버튼·Tooltip "준비 중"·"24시간 이내 삭제" 문구 → 실 정책 문구로 교체.
//           EmailScreen(F): DELETE /users/me 구현 완료 — 이메일 변경 미제공 안내는 여전히 유지(OAuth IdP 의존).
//         MSC-FE-01: PasswordScreen(G) ?? 'kakao' fallback 제거 → useAuth().user.primaryProvider 직접 사용.
//           null 시 providerLabel 헬퍼가 '소셜 계정' 중립 반환. providerLabel 헬퍼는 SettingsChrome 에 추출.
//         MSC-FE-02: FAQ "카카오 외..." 카피 수정 — 실제 지원 provider(카카오·네이버·Google) 와 일치.
//         FlowLine b609867a FE 과업2: ExportScreen(I) — GET /users/me/export 구현 완료 → "준비 중"
//           비활성 카드를 실제 다운로드 버튼으로 교체(api/user-export.client.ts, 헤더 주석 참조).
// [사이드 이팩트] usePatchUserMe 성공 시 queryKeys.user.me() invalidate → useMe 재조회.
//                 useMe 로딩 중 MUI Skeleton 표시 (ProfileEditScreen / TasteScreen).
//                 notificationPrefs 알림 토글은 SettingsPage.tsx 에서 처리.
//                 ProfileEditScreen 의 avatarUrl 은 useMe 응답만 표시(편집 입력 없음).
//                 DeleteAccountScreen 탈퇴 성공 → window.location.href='/login' (React 트리 완전 초기화).
// [수정 시 고려사항] handle 변경 금지 (Q-12) — ProfileEditScreen 에서 read-only 처리.
//                   locale 옵션 추가 시 LOCALE_OPTIONS 배열 갱신 (BCP47 코드).
//                   links 는 Record<string, url> — 현재 단일 URL 입력 (key='website' 고정).
//                   아바타 이미지 업로드 endpoint 추가 시 별도 Spec 으로 ProfileEditScreen 의 아바타 영역에 업로드 UI 부착.
//                   Apple IdP 지원 시 FAQ 카피("준비 중이에요" 문구) + providerLabel 맵 동시 갱신.

import { Box, ButtonBase, MenuItem, Select, Skeleton, TextField, Typography } from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import { AVATAR_UPLOAD_MAX_BYTES } from '@gangwoncanvas/types';
import type { AvatarUploadKind, AvatarUploadContentType } from '@gangwoncanvas/types';
import { useState, useEffect, useRef, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMe } from '../../hooks/useUserProfile.js';
import { usePatchUserMe } from '../../hooks/usePatchUserMe.js';
import { useDeleteAccount } from '../../hooks/useDeleteAccount.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useToast } from '../../components/feedback/Toast.js';
import { ApiError } from '../../api/http.js';
import { exportMyData } from '../../api/user-export.client.js';
import { requestAvatarUploadUrl } from '../../api/user.client.js';
import { stripImageExif, StripImageExifError } from '../../utils/stripImageExif.js';

import {
  SetHeader,
  ST_BG1,
  ST_BORDER,
  ST_BRAND,
  ST_GREEN,
  ST_TEXT_HI,
  ST_TEXT_LOW,
  ST_TEXT_MID,
  providerLabel,
} from './SettingsChrome.js';

// ─────────────────────────────────────────────
// 1) 프로필 편집 — BE 실연결 (B)
// ─────────────────────────────────────────────

// [목적] locale 옵션 — Select dropdown 표시 + BE 저장값
// [이유] 2026-05-17 profile-edit-polish — 텍스트 입력은 사용자가 BCP47 코드 직접 입력해야 해서 UX 불친절
// [사이드 이팩트] BE patchUserMeInputSchema.locale 은 min(2).max(10) — 본 배열 값들은 전부 통과
// [수정 시 고려사항] 신규 언어 추가 시 본 배열에 한 줄 추가 + i18n 번들 파일 동시 갱신
const LOCALE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'ko', label: '한국어' },
  { value: 'en', label: 'English' },
  { value: 'ja', label: '日本語' },
];

// [목적] 아바타/커버 업로드 중 오버레이 배경·전경색 — §6-3 디자인 토큰 강제(직접 hex 금지) 준수
// [이유] tokens.color.overlay.scrimOnBrand 는 정확히 "이미지 위 반투명 어두운 배경 + 흰 텍스트" 용도로
//        이미 정의된 토큰(FolioMobile 커버 헤더 칩과 동일 근거 — 어떤 이미지 위에서도 흰 텍스트 대비 확보).
// [사이드 이팩트] 없음(상수 파생).
// [수정 시 고려사항] 이 파일은 다크모드 분기가 없는 단일(light) 테마 화면이므로 'light' 고정 — 다크모드
//                   지원 시 useColorMode() 등으로 모드를 주입해 재계산할 것.
const UPLOAD_OVERLAY_BG = tokens.color.overlay.scrimOnBrand.light;
const UPLOAD_OVERLAY_FG = tokens.color.surface[0].light;

// [목적] kind(avatar/cover) 별 재인코딩 시 긴 변 축소 상한(px)
// [이유] 보안 리뷰 M-2 조치 — 아바타는 작은 원형 표시 용도라 1024px, 커버는 넓은 배너라 2048px 이면
//        충분하고 용량 상한(2MB/5MB) 충족에도 도움이 된다.
// [사이드 이팩트] stripImageExif 호출 시 이 값을 maxDimension 인자로 전달.
// [수정 시 고려사항] 값 변경 시 packages/types AVATAR_UPLOAD_MAX_BYTES 와 별개 축(해상도 vs 용량)이므로
//                   동시 검토하되 서로 강제 동기화할 필요는 없음.
const IMAGE_MAX_DIMENSION: Record<AvatarUploadKind, number> = {
  avatar: 1024,
  cover: 2048,
};

// [목적] ProfileEditScreen 아바타 미리보기(이미지/이니셜 플레이스홀더) 공통 치수
// [이유] 2026-09-20 void-element 버그 수정으로 아바타 렌더가 <img> / <div> 두 JSX 분기로 갈라지면서
//        치수·테두리가 중복 정의될 위험이 생겼다 — 단일 소스로 묶어 분기 간 시각적 드리프트를 막는다.
// [사이드 이팩트] 없음(상수).
// [수정 시 고려사항] 96px 변경 시 위쪽 Skeleton(variant="circular" width/height) 도 함께 갱신해야
//                   로딩 → 로드 완료 전환에서 레이아웃이 튀지 않는다.
const AVATAR_PREVIEW_SX = {
  width: 96,
  height: 96,
  borderRadius: '50%',
  border: `4px solid ${ST_BG1}`,
  boxShadow: '0 6px 16px rgba(26,77,143,.25)',
} as const;

/**
 * [목적] ProfileEditScreen — useMe 로 초기값 로드, usePatchUserMe 로 저장
 * [이유] settings-be-wire 작업 B — 기존 mock 동작을 실 BE 연결로 교체.
 *         2026-05-17 profile-edit-polish — 아바타 URL 입력 제거 + locale Select dropdown 도입.
 *         2026-09-18 FlowLine b609867a 잔여(프로필 사진·커버 업로드) — 아바타/커버 탭-업로드 UI 연결
 *         (POST /users/me/avatar-upload-url presign → 실 PUT → patchMe 확정).
 *         2026-09-18 security M-2/L-2 (docs/security/2026-09-18_storage-and-image-upload.md) —
 *         (1) 업로드 전 stripImageExif 로 canvas 재인코딩(EXIF/GPS 제거 + 긴 변 축소 avatar 1024px·
 *             cover 2048px), presign/PUT 은 재인코딩된 File 의 실제 byte 크기·MIME 으로 수행
 *             (서버가 서명한 Content-Length 와 정확히 일치해야 하므로 원본 file.size 사용 금지).
 *         (2) 커버 이미지를 CSS backgroundImage:url() 문자열 보간 대신 <img>(alt="", 장식용) 로 교체 —
 *             coverUrl 값에 의한 CSS 선언 주입 가능성 제거.
 * [사이드 이팩트] 저장 성공 시 useMe 캐시 invalidate (usePatchUserMe 내부 처리) — 업로드 확정도 동일 경로.
 *                 handle 은 read-only (Q-12 정책). bio 표시 버그는 BE DDL 11_users_profile_extension.sql
 *                 추가로 동시 해소. 디코드 실패(손상 파일)나 재인코딩 후에도 상한 초과 시 업로드를 차단하고
 *                 원본을 그대로 올리는 폴백은 하지 않는다(EXIF 잔존 방지).
 * [수정 시 고려사항] bio maxLength 는 patchUserMeInputSchema.bio.max(400) 과 동기화 필요.
 *                   displayName maxLength 는 schema.max(50) 과 동기화.
 *                   links 는 Record<string,url> — 현재 'website' 키 단일 입력으로 단순화.
 *                   S3 미설정(BE 501) 환경에서는 handleImageUpload 가 기존 "이미지 업로드는 v1.1" 문구를
 *                   토스트로 그대로 노출한다(사전에 가용성을 알 방법이 없어 시도 시점에 판별 — ponytail:
 *                   가용성 사전 조회 엔드포인트는 두지 않음, 필요해지면 GET /users/me/avatar-upload-url/health 등 추가).
 *                   IMAGE_MAX_DIMENSION 상한 변경 시 stripImageExif 호출부(handleImageUpload) 동시 갱신.
 */
export function ProfileEditScreen(): JSX.Element {
  const navigate = useNavigate();
  const { me, isLoading } = useMe();
  const { mutate: patchMe, mutateAsync: patchMeAsync, isPending } = usePatchUserMe();
  const toast = useToast();

  // 로컬 폼 상태 — useMe 로드 후 초기화
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [locale, setLocale] = useState('ko');
  const [website, setWebsite] = useState('');
  // [목적] 아바타/커버 이미지 업로드 진행 상태 — 파일 선택→presign→PUT→PATCH 확정 구간 동안 true
  // [이유] FlowLine b609867a 잔여 — 계약: "진행률 없이 로딩만" 표시. 종류별로 분리해 한쪽 업로드 중에도
  //        다른 쪽 클릭을 막지 않는다.
  // [사이드 이팩트] 없음(로컬 UI 상태).
  // [수정 시 고려사항] 업로드 성공 시 usePatchUserMe 가 useMe 캐시를 invalidate — 별도 로컬 미리보기 상태 불필요.
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  // [목적] useMe 데이터 로드 완료 시 폼 초기값 설정
  // [이유] 서버 데이터가 비동기로 도착하므로 useEffect 로 동기화
  // [사이드 이팩트] me 변경마다 폼 재설정 — 저장 후 invalidate 시 서버 최신값으로 덮어씀
  // [수정 시 고려사항] 사용자가 입력 중일 때 invalidate 로 덮어쓰는 경우 → isPending 중 재설정 방지
  useEffect(() => {
    if (!me) return;
    setDisplayName(me.displayName ?? '');
    setBio(me.bio ?? '');
    setLocale(me.locale ?? 'ko');
    setWebsite((me.links as Record<string, string> | null)?.['website'] ?? '');
  }, [me]);

  // [목적] 변경된 필드만 PATCH body 로 전송
  // [이유] BE PATCH /users/me 는 partial merge — 불필요한 필드 전송 금지
  // [사이드 이팩트] usePatchUserMe 가 성공 후 toast + invalidate 처리
  // [수정 시 고려사항] links 가 빈 문자열이면 null 전송하여 서버 측 제거 처리. avatarUrl 은 본 화면 비편집.
  const handleSave = (): void => {
    if (!me) return;
    const patch: Parameters<typeof patchMe>[0] = {};

    if (displayName !== me.displayName) patch.displayName = displayName || undefined;
    if (bio !== (me.bio ?? '')) patch.bio = bio || null;
    if (locale !== me.locale) patch.locale = locale || undefined;

    const currentWebsite = (me.links as Record<string, string> | null)?.['website'] ?? '';
    if (website !== currentWebsite) {
      patch.links = website ? { website } : null;
    }

    if (Object.keys(patch).length === 0) {
      navigate(-1);
      return;
    }

    patchMe(patch);
  };

  // [목적] 아바타/커버 이미지 업로드 — stripImageExif 재인코딩(EXIF 제거) → presigned URL 발급 → 실 PUT
  //        → patchMe 확정
  // [이유] FlowLine b609867a 잔여 — POST /users/me/avatar-upload-url 계약 연결. 진행률 없이 로딩 상태만 표시.
  //        보안 리뷰 M-2 — 원본 File 을 그대로 PUT 하면 EXIF(GPS) 가 공개 버킷에 영구 노출되므로
  //        canvas 재인코딩을 presign 요청보다 먼저 수행한다.
  // [사이드 이팩트] 성공 시 patchMeAsync 가 useMe 캐시 invalidate + "저장되었습니다" 토스트(usePatchUserMe 내부).
  //                S3 미설정(501) 시 기존 "이미지 업로드는 v1.1" 안내 문구를 토스트로 노출(정적 문구 대체).
  // [수정 시 고려사항] presign 의 contentType/sizeBytes 와 실 PUT body 는 반드시 stripImageExif 가 반환한
  //                   File(stripped) 기준이어야 한다 — 원본 file.size/file.type 을 쓰면 서버 서명
  //                   Content-Length 와 불일치해 PUT 이 403 난다. 용량 상한(AVATAR_UPLOAD_MAX_BYTES,
  //                   packages/types 단일 소스)은 재인코딩 후 크기로 재검증 — 원본이 상한을 넘어도
  //                   축소 후 상한을 만족하면 통과시키는 것이 의도(디코드 실패·재인코딩 후 상한 초과만 차단).
  const handleImageUpload = async (kind: AvatarUploadKind, file: File): Promise<void> => {
    const setUploading = kind === 'avatar' ? setAvatarUploading : setCoverUploading;
    setUploading(true);
    try {
      const stripped = await stripImageExif(file, IMAGE_MAX_DIMENSION[kind]);

      const maxBytes = AVATAR_UPLOAD_MAX_BYTES[kind];
      if (stripped.size > maxBytes) {
        toast.show(
          `이미지 용량이 너무 커요 (최대 ${Math.floor(maxBytes / 1024 / 1024)}MB)`,
          'error',
        );
        return;
      }

      const { uploadUrl, publicUrl } = await requestAvatarUploadUrl({
        kind,
        contentType: stripped.type as AvatarUploadContentType,
        sizeBytes: stripped.size,
      });

      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': stripped.type },
        body: stripped,
      });
      if (!putRes.ok) throw new Error(`S3 PUT 실패 (status=${putRes.status})`);

      await patchMeAsync(kind === 'avatar' ? { avatarUrl: publicUrl } : { coverUrl: publicUrl });
    } catch (err) {
      if (err instanceof StripImageExifError) {
        toast.show(
          err.reason === 'unsupported-type'
            ? '지원하지 않는 이미지 형식이에요 (jpg·png·webp만 가능해요)'
            : '이미지를 처리할 수 없어요. 손상되지 않은 파일로 다시 시도해 주세요.',
          'error',
        );
      } else if (err instanceof ApiError && err.status === 501) {
        toast.show('이미지 업로드는 v1.1 에서 제공됩니다', 'error');
      } else {
        toast.show('이미지 업로드에 실패했어요. 잠시 후 다시 시도해 주세요.', 'error');
      }
    } finally {
      setUploading(false);
    }
  };

  // [목적] 파일 선택 input onChange — 선택된 파일로 업로드 트리거 + 같은 파일 재선택 허용
  // [이유] value 를 비워두지 않으면 동일 파일을 다시 선택했을 때 onChange 가 발생하지 않는다(브라우저 기본 동작).
  // [사이드 이팩트] 없음.
  const handleAvatarFileChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) void handleImageUpload('avatar', file);
  };
  const handleCoverFileChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) void handleImageUpload('cover', file);
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: ST_BG1 }}>
      <SetHeader
        title="프로필 편집"
        right={
          <ButtonBase
            onClick={handleSave}
            disabled={isPending || isLoading}
            aria-label="저장"
            sx={{
              color: isPending || isLoading ? ST_TEXT_LOW : ST_BRAND,
              fontSize: 12,
              fontWeight: 700,
              p: '4px 6px',
            }}
          >
            {isPending ? '저장 중...' : '저장'}
          </ButtonBase>
        }
      />
      {/* [목적] 폼 본문 — 모바일은 풀폭, 데스크톱은 중앙 정렬된 560px 칼럼
        [이유] 2026-09-20: 기존에는 모바일 기준 풀폭 폼만 있어 데스크톱(사이드바 제외 1400px+)에서
               닉네임 입력칸 하나가 화면 끝까지 늘어나 시선 이동 거리가 과도했다. SettingsPage 가
               이미 maxWidth+mx:auto 로 중앙 정렬하므로 동일 관례를 따른다(폼 1열이라 더 좀게).
        [사이드 이팩트] 없음 — xs 브레이크포인트에서는 maxWidth 가 뷰포트보다 커서 기존 모바일 레이아웃 불변.
        [수정 시 고려사항] SetHeader 는 전폭 고정(헤더 바) — 본문만 중앙 정렬한다. */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          width: '100%',
          maxWidth: { xs: '100%', md: 560 },
          mx: 'auto',
          p: { xs: '24px 18px 28px', md: '28px 20px 40px' },
        }}
      >
        {/* 아바타 영역 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: '28px' }}>
          <ButtonBase
            onClick={() => avatarInputRef.current?.click()}
            disabled={isLoading || avatarUploading}
            aria-label="프로필 사진 변경"
            sx={{
              position: 'relative',
              borderRadius: '50%',
              '&:focus-visible': { outline: `2px solid ${ST_BRAND}`, outlineOffset: 2 },
            }}
          >
            {isLoading ? (
              <Skeleton variant="circular" width={96} height={96} />
            ) : me?.avatarUrl ? (
              // [목적] 아바타 이미지 렌더 — <img> 는 void 요소이므로 children 을 절대 넘기지 않는다
              // [이유] 버그(2026-09-20): 기존 `component={me?.avatarUrl ? 'img' : 'div'}` 단일 Box 는
              //        avatarUrl 이 있을 때 children 자리에 `false` 를 넘겨 React assertValidProps 가
              //        "img is a void element tag and must neither have `children`..." 로 throw →
              //        /settings/profile 진입 자체가 라우터 에러 화면으로 대체되어 프로필 편집이
              //        아예 열리지 않았다(children 이 false 여도 `props.children != null` 이므로 throw).
              // [사이드 이팩트] 이미지/플레이스홀더 분기를 JSX 레벨로 분리 — 시각적 결과는 기존 의도와 동일.
              // [수정 시 고려사항] 두 분기의 공통 치수(96px/원형/테두리)는 AVATAR_PREVIEW_SX 단일 소스에서
              //                   가져온다. 크기 변경 시 그 상수만 수정할 것.
              <Box
                component="img"
                src={me.avatarUrl}
                alt=""
                loading="lazy"
                aria-hidden="true"
                sx={{ ...AVATAR_PREVIEW_SX, objectFit: 'cover' }}
              />
            ) : (
              <Box
                aria-hidden="true"
                sx={{
                  ...AVATAR_PREVIEW_SX,
                  background: 'linear-gradient(135deg,#2876D8,#1A4D8F)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: ST_BG1,
                  fontSize: 38,
                  fontWeight: 800,
                }}
              >
                {displayName.charAt(0) || 'K'}
              </Box>
            )}
            {avatarUploading && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  bgcolor: UPLOAD_OVERLAY_BG,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: UPLOAD_OVERLAY_FG,
                  fontSize: 10,
                  fontWeight: 700,
                }}
              >
                업로드 중...
              </Box>
            )}
          </ButtonBase>
          <input
            ref={avatarInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            aria-label="프로필 사진 파일 선택"
            onChange={handleAvatarFileChange}
            style={{ display: 'none' }}
          />
          <Typography sx={{ mt: '10px', color: ST_TEXT_LOW, fontSize: 11 }}>
            탭하여 프로필 사진을 변경하세요
          </Typography>
        </Box>

        {/* 커버 이미지 영역 */}
        <Box sx={{ mb: '18px' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: ST_TEXT_MID, mb: '7px' }}>
            커버 이미지
          </Typography>
          <ButtonBase
            onClick={() => coverInputRef.current?.click()}
            disabled={isLoading || coverUploading}
            aria-label="커버 이미지 변경"
            sx={{
              position: 'relative',
              width: '100%',
              height: 96,
              borderRadius: '11px',
              overflow: 'hidden',
              border: `1.5px solid ${ST_BORDER}`,
              bgcolor: ST_BG1,
              '&:focus-visible': { outline: `2px solid ${ST_BRAND}`, outlineOffset: 2 },
            }}
          >
            {me?.coverUrl && (
              // [목적] 커버 이미지 표시 — <img> 요소로 렌더링(장식용, alt="")
              // [이유] 보안 리뷰 L-2 — 이전에는 sx.backgroundImage 에 coverUrl 을 `url(${...})` 문자열로
              //        직접 보간해 CSS 선언 주입이 가능했다(본인 화면 한정이지만 방어적으로 제거).
              //        <img src> 는 URL 을 CSS 선언이 아닌 속성값으로만 사용해 주입 경로가 없다.
              // [사이드 이팩트] 없음(시각적으로 기존 backgroundSize:cover/position:center 와 동일).
              // [수정 시 고려사항] 순수 장식 이미지(대체 텍스트는 위쪽 "커버 이미지" 라벨이 이미 제공)이므로
              //                   alt="" 유지 — 스크린리더 중복 낭독 방지.
              <Box
                component="img"
                src={me.coverUrl}
                alt=""
                loading="lazy"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            )}
            {!me?.coverUrl && !isLoading && (
              <Typography sx={{ fontSize: 11, color: ST_TEXT_LOW }}>
                탭하여 커버 이미지를 추가하세요
              </Typography>
            )}
            {isLoading && <Skeleton variant="rectangular" width="100%" height={96} />}
            {coverUploading && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  bgcolor: UPLOAD_OVERLAY_BG,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: UPLOAD_OVERLAY_FG,
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                업로드 중...
              </Box>
            )}
          </ButtonBase>
          <input
            ref={coverInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            aria-label="커버 이미지 파일 선택"
            onChange={handleCoverFileChange}
            style={{ display: 'none' }}
          />
        </Box>

        {/* 닉네임 */}
        <FormField label="닉네임" isLoading={isLoading} counter={`${displayName.length}/50`}>
          <TextField
            fullWidth
            value={displayName}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setDisplayName(e.target.value.slice(0, 50))
            }
            inputProps={{ 'aria-label': '닉네임', maxLength: 50 }}
            sx={inputSx}
          />
        </FormField>

        {/* 아이디 — read-only (Q-12) */}
        <Box sx={{ mb: '18px' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: ST_TEXT_MID, mb: '7px' }}>
            아이디
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              bgcolor: ST_BG1,
              border: `1.5px solid ${ST_BORDER}`,
              borderRadius: '11px',
              p: '12px 14px',
            }}
          >
            <Box
              component="span"
              sx={{ fontSize: 13, color: ST_TEXT_LOW, fontFamily: 'JetBrains Mono, monospace' }}
            >
              @
            </Box>
            {isLoading ? (
              <Skeleton width={140} height={18} />
            ) : (
              <Box
                component="span"
                sx={{
                  flex: 1,
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: 'JetBrains Mono, monospace',
                  color: ST_TEXT_HI,
                }}
              >
                {me?.handle ?? ''}
              </Box>
            )}
            <Box
              component="span"
              sx={{
                fontSize: 10,
                color: ST_GREEN,
                fontWeight: 700,
                bgcolor: '#E8F8EF',
                borderRadius: '5px',
                px: '7px',
                py: '2px',
              }}
            >
              변경 불가
            </Box>
          </Box>
          <Typography sx={{ fontSize: 10, color: ST_TEXT_LOW, mt: '5px' }}>
            아이디는 변경할 수 없습니다 (Q-12 정책)
          </Typography>
        </Box>

        {/* 자기소개 */}
        <FormField label="자기소개" isLoading={isLoading} counter={`${bio.length}/400`}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={bio}
            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              setBio(e.target.value.slice(0, 400))
            }
            inputProps={{ 'aria-label': '자기소개', maxLength: 400 }}
            sx={inputSx}
          />
        </FormField>

        {/* 웹사이트 URL */}
        <FormField label="웹사이트" isLoading={isLoading}>
          <TextField
            fullWidth
            value={website}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setWebsite(e.target.value)}
            placeholder="https://mysite.com"
            inputProps={{ 'aria-label': '웹사이트 URL', type: 'url' }}
            sx={inputSx}
          />
        </FormField>

        {/* 언어(locale) — Select dropdown */}
        <FormField label="언어" isLoading={isLoading}>
          <Select
            fullWidth
            value={locale}
            onChange={(e) => setLocale(e.target.value)}
            inputProps={{ 'aria-label': '언어' }}
            sx={{
              ...inputSx,
              '& .MuiSelect-select': { py: '10px' },
            }}
          >
            {LOCALE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormField>

        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '11px',
            p: '12px 14px',
            border: `1px solid ${ST_BORDER}`,
            display: 'flex',
            gap: '9px',
            alignItems: 'flex-start',
          }}
        >
          <Box component="span" sx={{ fontSize: 14 }}>
            💡
          </Box>
          <Typography sx={{ fontSize: 11, color: ST_TEXT_MID, lineHeight: 1.55 }}>
            다른 사용자에게 보여지는 정보예요. 프로필 사진·커버 이미지는 jpg·png·webp, 각각 최대
            2MB·5MB 까지 업로드할 수 있어요.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ─── 폼 필드 헬퍼 컴포넌트 ─────────────────────────────────────────────────────

function FormField({
  label,
  isLoading,
  counter,
  children,
}: {
  label: string;
  isLoading: boolean;
  counter?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Box sx={{ mb: '18px' }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: ST_TEXT_MID, mb: '7px' }}>
        {label}
      </Typography>
      {isLoading ? (
        <Skeleton variant="rectangular" height={44} sx={{ borderRadius: '11px' }} />
      ) : (
        children
      )}
      {counter && !isLoading && (
        <Typography
          sx={{
            fontSize: 10,
            color: ST_TEXT_LOW,
            mt: '5px',
            textAlign: 'right',
            fontFamily: 'JetBrains Mono, monospace',
          }}
        >
          {counter}
        </Typography>
      )}
    </Box>
  );
}

// ─── 공통 TextField sx ──────────────────────────────────────────────────────────
const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '11px',
    bgcolor: '#fff',
    fontSize: 13,
    fontWeight: 600,
    color: ST_TEXT_HI,
    '& fieldset': { borderColor: ST_BORDER, borderWidth: 1.5 },
    '&:hover fieldset': { borderColor: ST_BORDER },
    '&.Mui-focused fieldset': { borderColor: ST_BRAND },
    '&:focus-visible': { outline: `2px solid ${ST_BRAND}`, outlineOffset: 2 },
  },
};

// ─────────────────────────────────────────────
// 2) 이메일 — BE 미제공 안내 (F)
// ─────────────────────────────────────────────

/**
 * [목적] EmailScreen — BE 미제공 안내 카드 (이메일 변경은 OAuth IdP 의존)
 * [이유] settings-be-wire 작업 F — 이메일 변경 endpoint 없음. 소셜 로그인 전용 서비스이므로
 *         이메일 변경은 각 IdP(카카오/Apple/Google/네이버) 에서 직접 수행.
 *         fe-be-gap-tier3 T14: DELETE /users/me 구현 완료 — 관련 주석 갱신(이메일 화면에서 참조하던 미구현 메모 제거).
 * [사이드 이팩트] 기존 2단계 입력 폼 제거. useMe 로 emailMasked + primaryProvider 표시.
 * [수정 시 고려사항] BE 에 이메일 변경 endpoint 추가 시 이 화면을 본래 폼으로 복구.
 *                   OAuth 연동 변경은 re-auth 플로우 포함 — 별도 Spec.
 */
export function EmailScreen(): JSX.Element {
  const navigate = useNavigate();
  const { me, isLoading } = useMe();

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: ST_BG1 }}>
      <SetHeader title="이메일 변경" />
      <Box sx={{ flex: 1, overflowY: 'auto', p: '24px 18px 28px' }}>
        {/* 현재 이메일 카드 */}
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '13px',
            p: '14px 16px',
            border: `1px solid ${ST_BORDER}`,
            mb: '18px',
          }}
        >
          <Typography
            sx={{
              fontSize: 10.5,
              color: ST_TEXT_LOW,
              fontWeight: 600,
              letterSpacing: '0.4px',
              textTransform: 'uppercase',
              mb: '5px',
            }}
          >
            현재 이메일
          </Typography>
          {isLoading ? (
            <Skeleton width={200} height={22} />
          ) : (
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: ST_TEXT_HI,
                fontFamily: 'JetBrains Mono, monospace',
                mb: '6px',
              }}
            >
              {me?.emailMasked ?? '—'}
            </Typography>
          )}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              bgcolor: '#FFF8E1',
              color: '#8B6914',
              borderRadius: '5px',
              px: '7px',
              py: '2px',
              fontSize: 9.5,
              fontWeight: 700,
            }}
          >
            <Box component="span">✓</Box>
            <Box component="span">소셜 계정 연동</Box>
          </Box>
        </Box>

        {/* 안내 카드 */}
        <Box
          sx={{
            bgcolor: '#EAF4FF',
            borderRadius: '13px',
            p: '16px',
            border: `1px solid #B8D8F8`,
            mb: '24px',
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: ST_TEXT_HI, mb: '8px' }}>
            이메일 변경
          </Typography>
          <Typography sx={{ fontSize: 12, color: ST_TEXT_MID, lineHeight: 1.65 }}>
            이메일은 연결된 소셜 계정의 정보입니다. 변경하시려면 카카오 / Apple / Google / 네이버
            계정 설정에서 직접 변경해 주세요.
          </Typography>
        </Box>

        <ButtonBase
          onClick={() => navigate(-1)}
          aria-label="닫기"
          sx={{
            width: '100%',
            bgcolor: ST_BG1,
            border: `1.5px solid ${ST_BORDER}`,
            color: ST_TEXT_HI,
            borderRadius: '11px',
            p: '13px',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          닫기
        </ButtonBase>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────
// 3) 비밀번호 변경 — BE 미제공 안내 (G)
// ─────────────────────────────────────────────

/**
 * [목적] PasswordScreen — primaryProvider 실데이터 표시 + BE 미제공 안내
 * [이유] settings-be-wire 작업 G — OAuth 전용 서비스로 비밀번호 자체가 없음. useMe 로 실 provider 표시.
 *         MSC-FE-01: 인라인 ?? 'kakao' fallback 제거 → useAuth().user.primaryProvider 직접 사용.
 *         null 시 providerLabel 헬퍼가 '소셜 계정' 중립 반환 — 카카오 오표시 금지.
 * [사이드 이팩트] 기존 입력 폼 없음 (이미 소셜 안내 형태였음). primaryProvider 표시만 실데이터 적용.
 *                 providerLabel 헬퍼는 SettingsChrome.tsx 로 이전됨 (3화면 공통).
 * [수정 시 고려사항] Apple IdP 추가 시 SettingsChrome.providerLabel 맵 확장 필요.
 *                   me(GET /users/me)에 primaryProvider 미포함 → useAuth 로 조회.
 */
export function PasswordScreen(): JSX.Element {
  const navigate = useNavigate();
  const { isLoading } = useMe();
  const { user: authUser } = useAuth();

  // [목적] provider 식별자 → 사용자 친화적 레이블 변환
  // [이유] MSC-FE-01: ?? 'kakao' fallback 제거. null 시 '소셜 계정' 중립 표시.
  // [사이드 이팩트] providerLabel 헬퍼는 SettingsChrome 에 추출됨.
  // [수정 시 고려사항] BE 가 새 IdP 추가 시 SettingsChrome.providerLabel map 도 추가
  const resolvedProviderLabel = providerLabel(authUser?.primaryProvider ?? null);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: ST_BG1 }}>
      <SetHeader title="비밀번호 변경" />
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: '40px 22px 28px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '20px',
            bgcolor: '#FEE500',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 34,
            mb: '18px',
            boxShadow: '0 4px 12px rgba(254,229,0,.4)',
          }}
        >
          💬
        </Box>

        {isLoading ? (
          <Skeleton width={200} height={28} sx={{ mb: '8px' }} />
        ) : (
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 800,
              color: ST_TEXT_HI,
              mb: '8px',
              letterSpacing: '-0.3px',
              textAlign: 'center',
            }}
          >
            {resolvedProviderLabel} 계정으로 로그인 중
          </Typography>
        )}

        <Typography
          sx={{
            fontSize: 12,
            color: ST_TEXT_MID,
            lineHeight: 1.6,
            textAlign: 'center',
            mb: '24px',
          }}
        >
          OAuth 로 가입하신 계정은 비밀번호가 없습니다.
          <br />
          로그인은 항상 소셜 계정으로 진행됩니다.
        </Typography>

        <ButtonBase
          onClick={() => navigate(-1)}
          aria-label="닫기"
          sx={{
            width: '100%',
            bgcolor: ST_BG1,
            border: `1.5px solid ${ST_BORDER}`,
            color: ST_TEXT_HI,
            borderRadius: '11px',
            p: '13px',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          닫기
        </ButtonBase>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────
// 4) 여행 취향 — BE 실연결 (C)
// ─────────────────────────────────────────────

const TASTE_OPTIONS: ReadonlyArray<{ id: string; label: string; emoji: string; desc: string }> = [
  { id: 'nature', label: '자연·풍경', emoji: '🏔', desc: '산·바다·숲을 따라' },
  { id: 'food', label: '맛집·로컬', emoji: '🥘', desc: '그 지역의 맛' },
  { id: 'culture', label: '문화·예술', emoji: '🎨', desc: '박물관·전시·역사' },
  { id: 'active', label: '액티비티', emoji: '🚴', desc: '서핑·트레킹·자전거' },
  { id: 'cafe', label: '카페·뷰', emoji: '☕', desc: '분위기 좋은 공간' },
  { id: 'healing', label: '힐링·온천', emoji: '♨️', desc: '쉼이 있는 여행' },
];

/**
 * [목적] TasteScreen — useMe 로 초기 interests 로드, usePatchUserMe 로 저장
 * [이유] settings-be-wire 작업 C — 취향 토글 결과를 PATCH /users/me.interests 로 실저장
 * [사이드 이팩트] 저장 성공 시 useMe 캐시 invalidate (usePatchUserMe 내부 처리).
 *                 useMe 로딩 중 Skeleton 표시
 * [수정 시 고려사항] TASTE_OPTIONS 는 OnboardingPage 와 동일 배열 유지.
 *                   interests 가 비어있어도 저장 허용 (OnboardingPage 와 달리 강제 아님)
 */
export function TasteScreen(): JSX.Element {
  const { me, isLoading } = useMe();
  const { mutate: patchMe, isPending } = usePatchUserMe();

  const [tastes, setTastes] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);

  // [목적] useMe 로드 완료 시 interests 초기화 (1회)
  // [이유] 비동기 로드이므로 useEffect 로 동기화. 이후 변경은 로컬 state 우선
  // [사이드 이팩트] initialized 플래그로 저장 후 재로드 시 덮어쓰기 방지
  // [수정 시 고려사항] 저장 후 invalidate → useMe 재조회 → interests 재설정되면 사용자 입력 덮어씀 위험 → initialized 로 차단
  useEffect(() => {
    if (me && !initialized) {
      setTastes(me.interests ?? []);
      setInitialized(true);
    }
  }, [me, initialized]);

  const toggle = (id: string): void =>
    setTastes((t) => (t.includes(id) ? t.filter((x) => x !== id) : [...t, id]));

  const handleSave = (): void => {
    patchMe({ interests: tastes });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: ST_BG1,
        position: 'relative',
      }}
    >
      <SetHeader title="여행 취향" />
      <Box sx={{ flex: 1, overflowY: 'auto', p: '22px 18px 100px' }}>
        <Typography sx={{ fontSize: 13, color: ST_TEXT_MID, lineHeight: 1.55, mb: '18px' }}>
          관심 있는 여행 스타일을 선택하면
          <br />
          강원캔버스 추천이 더 정확해져요.
        </Typography>

        {isLoading ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '9px' }}>
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={90} sx={{ borderRadius: '14px' }} />
            ))}
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '9px' }}>
            {TASTE_OPTIONS.map((opt) => {
              const active = tastes.includes(opt.id);
              return (
                <ButtonBase
                  key={opt.id}
                  onClick={() => toggle(opt.id)}
                  aria-label={opt.label}
                  aria-pressed={active}
                  sx={{
                    bgcolor: active ? 'rgba(31,111,235,.06)' : '#fff',
                    border: `1.5px solid ${active ? ST_BRAND : ST_BORDER}`,
                    borderRadius: '14px',
                    p: '16px 12px',
                    textAlign: 'left',
                    position: 'relative',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    '&:focus-visible': { outline: `2px solid ${ST_BRAND}`, outlineOffset: 2 },
                  }}
                >
                  <Box sx={{ fontSize: 24, mb: '8px' }}>{opt.emoji}</Box>
                  <Typography
                    sx={{ fontSize: 12.5, fontWeight: 700, color: ST_TEXT_HI, mb: '3px' }}
                  >
                    {opt.label}
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: ST_TEXT_MID, lineHeight: 1.4 }}>
                    {opt.desc}
                  </Typography>
                  {active && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        bgcolor: ST_BRAND,
                        color: '#fff',
                        fontSize: 10,
                        fontWeight: 800,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      ✓
                    </Box>
                  )}
                </ButtonBase>
              );
            })}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          p: '12px 16px 16px',
          background: 'linear-gradient(180deg, transparent, rgba(246,248,251,0.95) 30%)',
        }}
      >
        <ButtonBase
          onClick={handleSave}
          disabled={isPending || isLoading}
          aria-label="저장"
          sx={{
            width: '100%',
            background:
              !isPending && !isLoading ? 'linear-gradient(135deg, #1F6FEB, #4A8AF0)' : '#D5DCE5',
            color: '#fff',
            borderRadius: '11px',
            p: '13px',
            fontSize: 13,
            fontWeight: 700,
            boxShadow: !isPending && !isLoading ? '0 4px 12px rgba(31,111,235,.3)' : 'none',
            '&:focus-visible': { outline: `2px solid ${ST_BRAND}`, outlineOffset: 2 },
          }}
        >
          {isPending ? '저장 중...' : `저장 (${tastes.length})`}
        </ButtonBase>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────
// 5) 데이터 내보내기 — BE 미제공 안내 (I)
// ─────────────────────────────────────────────

// [목적] ExportScreen 이 안내하는 포함 항목 목록 — GET /users/me/export 응답 필드와 1:1 대응
// [이유] FlowLine b609867a FE 과업2 — 사용자가 다운로드 전 무엇이 포함되는지 알 수 있어야 함(투명성).
// [사이드 이팩트] 없음(정적 표시 데이터).
// [수정 시 고려사항] BE 응답 필드(exportedAt/profile/folios/comments/likedFolioIds/bookmarkedFolioIds/
//                   following) 추가/변경 시 이 목록도 함께 갱신할 것.
const EXPORT_INCLUDES: ReadonlyArray<{ emoji: string; label: string }> = [
  { emoji: '👤', label: '프로필 정보' },
  { emoji: '🗂️', label: '내가 만든 폴리오' },
  { emoji: '💬', label: '내가 남긴 댓글' },
  { emoji: '♥', label: '좋아요·북마크한 폴리오' },
  { emoji: '🔗', label: '팔로잉 목록' },
];

/**
 * [목적] ExportScreen — 내 데이터(JSON) 다운로드 화면
 * [이유] FlowLine b609867a FE 과업2 — GET /users/me/export 실배선. 이전 "준비 중" 비활성 버튼을
 *        실제 다운로드 버튼으로 교체.
 * [사이드 이팩트] 성공 시 Blob → <a download> 클릭으로 브라우저 다운로드 트리거 + 성공 토스트.
 *                429(시간당 5회 초과)/401(비로그인)/기타 실패는 각각 다른 안내 토스트로 구분.
 * [수정 시 고려사항] envelope 없는 raw JSON 응답이라 user-export.client.ts 가 fetchWithEnvelope 를
 *                   경유하지 않음(헤더 주석 참조) — 이 화면은 그 클라이언트의 ApiError 만 소비.
 */
export function ExportScreen(): JSX.Element {
  const { show } = useToast();
  const [downloading, setDownloading] = useState(false);

  async function handleDownload(): Promise<void> {
    setDownloading(true);
    try {
      const { blob, filename } = await exportMyData();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      show('데이터 내보내기를 완료했어요.', 'success');
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        show('요청이 너무 많아요. 잠시 후 다시 시도해 주세요.', 'error');
      } else if (err instanceof ApiError && err.status === 401) {
        show('로그인이 필요해요.', 'error');
      } else {
        show('데이터 내보내기에 실패했어요. 잠시 후 다시 시도해 주세요.', 'error');
      }
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: ST_BG1 }}>
      <SetHeader title="내 데이터 내보내기" />
      <Box sx={{ flex: 1, overflowY: 'auto', p: '22px 18px 28px' }}>
        <Box
          sx={{
            bgcolor: ST_BG1,
            borderRadius: '13px',
            p: '20px 16px',
            border: `1px solid ${ST_BORDER}`,
            mb: '18px',
          }}
        >
          <Box sx={{ fontSize: 40, mb: '12px', textAlign: 'center' }}>📦</Box>
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 800,
              color: ST_TEXT_HI,
              mb: '10px',
              letterSpacing: '-0.3px',
              textAlign: 'center',
            }}
          >
            아래 항목이 JSON 파일로 내려받아져요
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {EXPORT_INCLUDES.map((item) => (
              <Box
                key={item.label}
                sx={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: 12.5 }}
              >
                <Box component="span" aria-hidden="true">
                  {item.emoji}
                </Box>
                <Typography sx={{ fontSize: 12.5, color: ST_TEXT_MID }}>{item.label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <ButtonBase
          onClick={handleDownload}
          disabled={downloading}
          aria-label={downloading ? '내 데이터 다운로드 중' : '내 데이터 다운로드(JSON)'}
          sx={{
            width: '100%',
            // [수정: 디자인 개편] 다운로드 진행 중에도 opacity만 낮춰 파란+흰색 버튼이 여전히 누를 수 있는 것처럼 보였다.
            // ST_BORDER 는 같은 파일 내 회원탈퇴 버튼이 비활성일 때 쓰는 것과 동일한 회색 톤.
            bgcolor: downloading ? ST_BORDER : ST_BRAND,
            color: downloading ? ST_TEXT_LOW : '#fff',
            borderRadius: '11px',
            p: '13px',
            fontSize: 13,
            fontWeight: 700,
            '&:focus-visible': { outline: `2px solid ${ST_BRAND}`, outlineOffset: 2 },
          }}
        >
          {downloading ? '다운로드 중…' : '내 데이터 다운로드(JSON)'}
        </ButtonBase>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────
// 6) 이용약관 / 개인정보 본문 — 정적 (J)
// ─────────────────────────────────────────────

// [목적] 약관 정적 본문 데이터 — BE 연결 없는 정적 페이지
// [이유] 법무 검토 후 최종 본문 교체 예정 — 현재는 예시 문안
// [사이드 이팩트] 없음
// [수정 시 고려사항] 실제 서비스 출시 전 법무 검토 후 본문 교체 필수.
//                   [QA-P2-12] 하단 "예시 문안입니다" 안내는 검토 전 문구를 확정본처럼 보이지 않게 하는 정직한 표시다 —
//                   법무 검토본으로 교체하기 전에는 임의로 제거하지 말 것(PolicyScreen.test.tsx 가 이를 고정한다).
//                   수집 항목의 로그인 수단은 apps/api ACTIVE_OAUTH_PROVIDERS(kakao·naver·google)와 일치해야 한다.
const POLICIES = {
  tos: {
    title: '이용약관',
    version: 'v1.0 · 2025.01.15',
    sections: [
      {
        h: '제1조 (목적)',
        b: '본 약관은 강원캔버스(이하 "회사")가 제공하는 여행 폴리오 서비스(이하 "서비스") 이용에 관한 조건과 절차, 이용자와 회사의 권리·의무·책임사항을 규정함을 목적으로 합니다.',
      },
      {
        h: '제2조 (서비스의 내용)',
        b: '회사는 다음과 같은 서비스를 제공합니다.\n· 대화 기반 강원 여행 일정 생성 및 추천\n· 관광지·숨은 명소·맛집 정보 조회\n· 개인화된 여행 폴리오 저장 및 공유',
      },
      {
        h: '제3조 (이용자의 의무)',
        b: '이용자는 다음 행위를 하여서는 안 됩니다.\n1. 타인의 정보 도용\n2. 회사가 게시한 정보의 변경\n3. 회사와 기타 제3자의 저작권 등 지적재산권을 침해하는 행위',
      },
      {
        h: '제4조 (서비스 변경 및 중단)',
        b: '회사는 운영상·기술적 필요에 따라 제공 중인 서비스를 변경할 수 있으며, 이 경우 변경 사유와 내용을 서비스 내 공지합니다.',
      },
      {
        h: '제5조 (책임의 제한)',
        b: '회사는 이용자가 서비스를 통해 얻은 자료로 인한 손해에 대하여 책임을 지지 않습니다. 서비스에서 제공되는 관광지 정보는 참고용이며, 실제 이용 조건과 다를 수 있습니다.',
      },
    ],
  },
  privacy: {
    title: '개인정보 처리방침',
    version: 'v1.0 · 2025.01.15',
    sections: [
      {
        h: '1. 수집 항목',
        // [수정: QA-P2-12] 로그인 수단을 실제 제공 범위(카카오·네이버·Google)에 맞춘다 — 종전엔 카카오 계정만 적혀 있었다.
        b: '· 필수: 소셜 로그인(카카오·네이버·Google) 계정 식별자, 닉네임, 이메일\n· 선택: 프로필 사진, 여행 취향, 생성된 폴리오',
      },
      {
        h: '2. 수집 목적',
        b: '· 회원 식별 및 로그인\n· 개인화된 여행 추천 및 서비스 제공\n· 고객 문의 응대 및 공지사항 전달',
      },
      {
        h: '3. 보유 기간',
        b: '회원 탈퇴 시 즉시 파기. 단 관계 법령에 의해 보존 필요가 있는 경우 해당 기간 동안 보관합니다.\n· 전자상거래법: 5년\n· 통신비밀보호법: 3개월',
      },
      {
        h: '4. 동의하지 않을 권리',
        b: '이용자는 개인정보 수집·이용에 동의하지 않을 권리가 있으며, 동의하지 않을 경우 서비스 이용이 제한될 수 있습니다.',
      },
      {
        h: '5. 제3자 제공',
        b: '회사는 이용자의 개인정보를 제3자에게 제공하지 않습니다. 단, 이용자의 사전 동의가 있거나 법령의 규정에 의한 경우는 예외로 합니다.',
      },
    ],
  },
} as const;

export function PolicyScreen({ kind = 'tos' }: { kind?: 'tos' | 'privacy' } = {}): JSX.Element {
  const data = POLICIES[kind];
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
      <SetHeader title={data.title} />
      {/* [목적] 약관 본문 컬럼 — 읽기 좋은 한 줄 길이로 폭 제한
          [이유] 2026-09-18 QA(M-7): 폭 제한이 없어 데스크톱에서 한 줄이 1190px(100자 이상)까지
                 늘어나 가독 한계(권장 45~75자)를 크게 넘겼다.
          [사이드 이팩트] 모바일(<720px)에서는 제한이 걸리지 않아 기존과 동일하게 보인다.
          [수정 시 고려사항] 같은 화면의 HelpScreen 은 카드 그리드라 폭 제한 대상이 아니다. */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: '18px 20px 28px', maxWidth: 720, width: '100%' }}>
        <Typography
          sx={{
            fontSize: 10,
            color: ST_TEXT_LOW,
            mb: '18px',
            fontFamily: 'JetBrains Mono, monospace',
          }}
        >
          {data.version}
        </Typography>
        {data.sections.map((s, i) => (
          <Box key={i} sx={{ mb: '20px' }}>
            <Typography
              sx={{
                fontSize: 12.5,
                fontWeight: 800,
                color: ST_TEXT_HI,
                mb: '7px',
                letterSpacing: '-0.2px',
              }}
            >
              {s.h}
            </Typography>
            <Typography
              sx={{ fontSize: 11.5, color: ST_TEXT_MID, lineHeight: 1.7, whiteSpace: 'pre-line' }}
            >
              {s.b}
            </Typography>
          </Box>
        ))}
        <Box
          sx={{
            bgcolor: ST_BG1,
            borderRadius: '10px',
            p: '12px 14px',
            fontSize: 10.5,
            color: ST_TEXT_LOW,
            lineHeight: 1.55,
            mt: '10px',
          }}
        >
          이 약관은 예시 문안입니다. 실제 서비스 출시 전 법무 검토를 거쳐 최종 본문으로 교체되어야
          합니다.
        </Box>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────
// 7) 고객센터 — 정적 (J)
// ─────────────────────────────────────────────

// [목적] FAQ 정적 데이터 — BE 연결 없음
// [이유] 정적 콘텐츠 페이지 — CMS 연동 별도 Spec
// [사이드 이팩트] 없음
// [수정 시 고려사항] CMS 연동 시 FAQ 배열을 API 응답으로 교체
const FAQS: ReadonlyArray<{ q: string; a: string }> = [
  {
    q: '폴리오는 어떻게 만들어지나요?',
    a: 'AI 컨셉 디자이너와 대화를 나누면 동반자, 일정, 무드 등을 파악해 자동으로 강원 여행 코스를 그려줍니다. 만들기 탭에서 시작할 수 있어요.',
  },
  {
    q: '생성된 폴리오를 수정할 수 있나요?',
    a: '네, 폴리오 하단의 AI 보정 바에서 "카페 더 추가해줘", "예산 줄여줘" 같은 자연어로 수정할 수 있어요.',
  },
  {
    q: '다른 사람의 폴리오를 가져올 수 있나요?',
    a: '폴리오 상단의 ⑂ Fork 버튼으로 내 컬렉션에 복제해서 자유롭게 수정할 수 있어요. 원작자 표기는 유지됩니다.',
  },
  {
    q: 'PDF로 저장하고 싶어요',
    a: '폴리오 우상단 PDF 버튼 또는 설정 › 내 폴리오 내보내기에서 가능해요. (현재 준비 중)',
  },
  {
    q: '반려견 동반 여행을 만들고 싶어요',
    a: '채팅에서 "🐾 반려견" 칩을 선택하거나 "강아지와 함께"라고 입력하시면 펫 프렌들리 코스만 추천해드려요.',
  },
  {
    q: '카카오 외 다른 계정으로 로그인하고 싶어요',
    a: '카카오·네이버·Google 로그인을 지원합니다. Apple 로그인은 준비 중이에요.',
  },
];

export function HelpScreen(): JSX.Element {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: ST_BG1 }}>
      <SetHeader title="고객센터" />
      <Box sx={{ flex: 1, overflowY: 'auto', p: '18px 16px 28px' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', mb: '18px' }}>
          {[
            { bg: '#EAF4FF', emoji: '💬', label: '1:1 문의', sub: '평일 24시간 내' },
            { bg: '#E8F8EF', emoji: '📢', label: '공지사항', sub: '새 기능 안내' },
          ].map((o) => (
            <ButtonBase
              key={o.label}
              aria-label={o.label}
              sx={{
                bgcolor: '#fff',
                border: `1px solid ${ST_BORDER}`,
                borderRadius: '12px',
                p: '14px 10px',
                display: 'flex',
                alignItems: 'center',
                gap: '9px',
                justifyContent: 'flex-start',
                textAlign: 'left',
                '&:focus-visible': { outline: `2px solid ${ST_BRAND}`, outlineOffset: 2 },
              }}
            >
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '10px',
                  bgcolor: o.bg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 15,
                }}
              >
                {o.emoji}
              </Box>
              <Box>
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: ST_TEXT_HI }}>
                  {o.label}
                </Typography>
                <Typography sx={{ fontSize: 9.5, color: ST_TEXT_MID, mt: '1px' }}>
                  {o.sub}
                </Typography>
              </Box>
            </ButtonBase>
          ))}
        </Box>

        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: ST_TEXT_MID,
            mb: '8px',
            px: '4px',
            letterSpacing: '0.3px',
            textTransform: 'uppercase',
          }}
        >
          자주 묻는 질문
        </Typography>
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '12px',
            border: `1px solid ${ST_BORDER}`,
            overflow: 'hidden',
            mb: '18px',
          }}
        >
          {FAQS.map((f, i) => (
            <Box
              key={i}
              sx={{ borderBottom: i < FAQS.length - 1 ? `1px solid ${ST_BORDER}` : 'none' }}
            >
              <ButtonBase
                onClick={() => setOpen(open === i ? null : i)}
                aria-label={f.q}
                aria-expanded={open === i}
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  p: '13px 14px',
                  textAlign: 'left',
                  justifyContent: 'flex-start',
                  '&:focus-visible': { outline: `2px solid ${ST_BRAND}`, outlineOffset: 2 },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontSize: 11,
                    color: ST_BRAND,
                    fontWeight: 800,
                    fontFamily: 'JetBrains Mono, monospace',
                    flexShrink: 0,
                  }}
                >
                  Q{String(i + 1).padStart(2, '0')}
                </Box>
                <Box
                  component="span"
                  sx={{
                    flex: 1,
                    fontSize: 12,
                    fontWeight: 600,
                    color: ST_TEXT_HI,
                    letterSpacing: '-0.2px',
                  }}
                >
                  {f.q}
                </Box>
                <Box
                  component="span"
                  aria-hidden="true"
                  sx={{
                    fontSize: 13,
                    color: ST_TEXT_LOW,
                    transform: open === i ? 'rotate(180deg)' : 'rotate(0)',
                    transition: 'transform 150ms',
                  }}
                >
                  ▾
                </Box>
              </ButtonBase>
              {open === i && (
                <Typography
                  sx={{
                    p: '2px 14px 14px 40px',
                    fontSize: 11.5,
                    color: ST_TEXT_MID,
                    lineHeight: 1.65,
                  }}
                >
                  {f.a}
                </Typography>
              )}
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '12px',
            p: '14px 16px',
            border: `1px solid ${ST_BORDER}`,
            fontSize: 11,
            color: ST_TEXT_MID,
            lineHeight: 1.65,
          }}
        >
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: ST_TEXT_HI, mb: '7px' }}>
            📞 운영 정보
          </Typography>
          <Box>· 운영 시간: 평일 10:00 – 18:00</Box>
          <Box>
            · 이메일:{' '}
            <Box
              component="a"
              href="mailto:help@gangwoncanvas.kr"
              sx={{ color: ST_BRAND, fontFamily: 'JetBrains Mono, monospace' }}
            >
              help@gangwoncanvas.kr
            </Box>
          </Box>
          <Box>· 점심: 12:00 – 13:00 (응답 지연)</Box>
        </Box>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────
// 8) 회원 탈퇴 — 실 플로우 활성 (H)
// ─────────────────────────────────────────────

// [목적] DeleteAccountScreen 전용 위험 색상 상수 — danger 토큰 기반
// [이유] fe-be-gap-tier3 T13 — 탈퇴 경고 카드 색상을 tokens.color.danger 에서 파생.
//         직접 hex 금지(§6-3) → 토큰 참조. danger.500 = #D73A49.
//         배경은 danger.500 alpha 4% 를 tokens 기반 rgba 로 표현.
// [수정 시 고려사항] danger 토큰 변경 시 이 상수도 자동 갱신(토큰 참조).
const _dc = tokens.color;
const _dL = 'light' as const;
const DANGER_FG = _dc.danger[500][_dL]; // #D73A49 — 경고 텍스트
const DANGER_BG = 'rgba(215,58,73,.06)'; // danger.500 기반 alpha 배경 (#D73A49 · 6%)
const DANGER_BORDER = 'rgba(215,58,73,.25)'; // danger.500 기반 alpha 테두리

/**
 * [목적] DeleteAccountScreen — 확인 단계(체크박스 동의) → DELETE /users/me → 로그아웃 전체 reload
 * [이유] fe-be-gap-tier3 T13(ADR-T3-4/-5) — DELETE /users/me 구현 완료.
 *         soft-delete: users.deleted_at=now() + 본인 폴리오 private 전환(공개 노출 즉시 중단).
 *         handle 재사용 불가(ADR-T3-5): 탈퇴 후에도 UNIQUE 점유 유지.
 *         세션 무효화: refresh 토큰 revoke + Redis 세션 삭제 + 쿠키 삭제 (BE 처리).
 * [사이드 이팩트] 탈퇴 성공 → useDeleteAccount.onSuccess → window.location.href='/login'.
 *                 React 트리 / QueryClient 완전 초기화 — SPA state 잔존 없음.
 *                 비활성 버튼·Tooltip "준비 중"·"24시간 이내 삭제" 문구 제거(T14).
 *                 하드삭제/익명화/데이터 export 는 별도 Spec(account-deletion-purge 가칭, Out-of-scope).
 * [수정 시 고려사항] 확인 단계(체크박스)는 안전장치 — 미체크 시 버튼 비활성.
 *                   탈퇴 실패(401/500 등) 시 isError=true → 에러 메시지 노출.
 *                   탈퇴 후 15분 잔존 access token 창은 refresh revoke 로 실질 차단(ADR-TR3-7 — 블랙리스트는 후속).
 */
// [수정: 디자인 개편] 회원탈퇴처럼 되돌릴 수 없는 액션은 체크박스 하나보다 높은 마찰을 요구해야 한다 —
// 확인 문구를 그대로 입력해야만 버튼이 활성화되도록 변경.
const DELETE_CONFIRM_PHRASE = '탈퇴합니다';

export function DeleteAccountScreen(): JSX.Element {
  const navigate = useNavigate();
  const [confirmText, setConfirmText] = useState('');
  const confirmed = confirmText.trim() === DELETE_CONFIRM_PHRASE;
  const { mutate: deleteAccount, isPending, isError, error } = useDeleteAccount();

  const handleDelete = (): void => {
    if (!confirmed) return;
    deleteAccount();
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: ST_BG1 }}>
      <SetHeader title="회원 탈퇴" />
      <Box sx={{ flex: 1, overflowY: 'auto', p: '22px 18px 28px' }}>
        {/* 탈퇴 정책 안내 카드 */}
        <Box
          role="region"
          aria-label="탈퇴 정책 안내"
          sx={{
            bgcolor: DANGER_BG,
            borderRadius: '13px',
            p: '14px 16px',
            mb: '18px',
            border: `1px solid ${DANGER_BORDER}`,
          }}
        >
          <Box
            sx={{
              fontSize: 13,
              fontWeight: 800,
              color: DANGER_FG,
              mb: '7px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <Box component="span" aria-hidden="true">
              ⚠️
            </Box>
            <Box component="span">탈퇴 전 꼭 확인하세요</Box>
          </Box>
          <Box
            component="ul"
            sx={{
              fontSize: 11.5,
              color: ST_TEXT_MID,
              lineHeight: 1.7,
              m: 0,
              p: 0,
              listStyle: 'none',
            }}
          >
            <Box component="li">· 계정은 즉시 비활성화되며 로그인이 불가능해집니다</Box>
            <Box component="li">· 작성한 폴리오가 비공개 전환되어 타인에게 노출이 중단됩니다</Box>
            <Box component="li">
              · 팔로워·팔로잉·좋아요·Fork·배지 기록이 보존됩니다 (soft-delete)
            </Box>
            <Box component="li">· 닉네임(handle)은 재사용이 불가합니다</Box>
          </Box>
          <Typography
            sx={{
              fontSize: 11,
              color: DANGER_FG,
              fontWeight: 700,
              mt: '10px',
              pt: '10px',
              borderTop: `1px solid ${DANGER_BORDER}`,
            }}
          >
            탈퇴 후 계정 복구는 지원되지 않습니다.
          </Typography>
        </Box>

        {/* 탈퇴 정책 상세 안내 */}
        <Box
          sx={{
            bgcolor: ST_BG1,
            borderRadius: '13px',
            p: '16px',
            border: `1px solid ${ST_BORDER}`,
            mb: '20px',
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: ST_TEXT_HI, mb: '8px' }}>
            탈퇴 후 데이터 처리 정책
          </Typography>
          <Typography sx={{ fontSize: 12, color: ST_TEXT_MID, lineHeight: 1.65 }}>
            탈퇴 시 계정은 즉시 비활성화(soft-delete)됩니다. 작성하신 폴리오·댓글·활동 내역은 서비스
            정책에 따라 일정 기간 보존된 후 삭제됩니다. 이 기간 동안 공개 노출은 차단됩니다.
          </Typography>
        </Box>

        {/* 확인 문구 입력 — 안전장치 */}
        {/* [수정: 디자인 개편] 단순 체크박스는 실수로 체크될 확률이 높아, "탈퇴합니다"를
            그대로 타이핑해야만 버튼이 활성화되도록 바꿈 — 되돌릴 수 없는 액션에 맞는 높은 마찰. */}
        <Box sx={{ mb: '24px' }}>
          <Typography
            component="label"
            htmlFor="delete-account-confirm-input"
            sx={{ display: 'block', fontSize: 12, color: ST_TEXT_HI, lineHeight: 1.55, mb: '8px' }}
          >
            계속하려면 아래에 <Box component="strong">"{DELETE_CONFIRM_PHRASE}"</Box>을 입력해
            주세요.
          </Typography>
          <TextField
            id="delete-account-confirm-input"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={DELETE_CONFIRM_PHRASE}
            fullWidth
            size="small"
            inputProps={{ 'aria-label': `확인을 위해 ${DELETE_CONFIRM_PHRASE} 입력` }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: ST_BG1,
                borderRadius: '11px',
                fontSize: 13,
                '& fieldset': {
                  borderColor: confirmed ? DANGER_BORDER : ST_BORDER,
                  borderWidth: 1.5,
                },
                '&:hover fieldset': { borderColor: confirmed ? DANGER_BORDER : ST_BORDER },
                '&.Mui-focused fieldset': { borderColor: DANGER_FG },
              },
            }}
          />
        </Box>

        {/* 에러 메시지 */}
        {isError && (
          <Box
            role="alert"
            sx={{
              fontSize: 12,
              color: DANGER_FG,
              fontWeight: 600,
              mb: '16px',
              p: '10px 14px',
              bgcolor: DANGER_BG,
              borderRadius: '8px',
              border: `1px solid ${DANGER_BORDER}`,
            }}
          >
            {(error as Error)?.message ?? '탈퇴 처리 중 오류가 발생했습니다. 다시 시도해 주세요.'}
          </Box>
        )}

        {/* 탈퇴 버튼 */}
        <ButtonBase
          onClick={handleDelete}
          disabled={!confirmed || isPending}
          aria-label="회원 탈퇴"
          aria-disabled={!confirmed || isPending}
          sx={{
            width: '100%',
            bgcolor: confirmed ? DANGER_FG : ST_BORDER,
            color: ST_BG1,
            borderRadius: '11px',
            p: '13px',
            fontSize: 13,
            fontWeight: 700,
            mb: '8px',
            opacity: confirmed ? 1 : 0.5,
            transition: 'opacity 150ms, background-color 150ms',
            '&:focus-visible': { outline: `2px solid ${DANGER_FG}`, outlineOffset: 2 },
            '&:disabled': { cursor: 'not-allowed' },
          }}
        >
          {isPending ? '탈퇴 처리 중…' : '회원 탈퇴'}
        </ButtonBase>

        <ButtonBase
          onClick={() => navigate(-1)}
          aria-label="취소"
          sx={{
            width: '100%',
            bgcolor: ST_BG1,
            border: `1.5px solid ${ST_BORDER}`,
            color: ST_TEXT_HI,
            borderRadius: '11px',
            p: '13px',
            fontSize: 13,
            fontWeight: 600,
            '&:focus-visible': { outline: `2px solid ${ST_BRAND}`, outlineOffset: 2 },
          }}
        >
          취소
        </ButtonBase>
      </Box>
    </Box>
  );
}
